import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Collapse,
  Grid,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { DayPicker } from 'react-day-picker';
import { format, isSameDay } from 'date-fns';
import 'react-day-picker/dist/style.css';
import { useSearch } from '../contexts/SearchContext';
import { useApi } from '../hooks/useApi';
import { RigAssessment, getAssessmentField } from '../types/RigAssessment';

interface AssessmentState { data?: RigAssessment; loading?: boolean; error?: string; }

const Forecast: React.FC = () => {
  const { reports, counts, loading, error, selectedDate, setSelectedDate, availableLocations, selectedLocation, setSelectedLocation } = useSearch();
  const { callApi } = useApi();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [assessments, setAssessments] = useState<Record<string, AssessmentState>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [queueing, setQueueing] = useState(false);
  const [showFalse, setShowFalse] = useState(false); // collapsed by default

  const open = Boolean(anchorEl);
  const handleDateClick = (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget);
  const handleDateClose = () => setAnchorEl(null);
  const handleDateSelect = (d: Date | undefined) => { if (d) setSelectedDate(d); handleDateClose(); };

  const rigs = useMemo(() => Array.from(new Set(reports.map(r => r.Rig).filter(Boolean))), [reports]);

  // Split rigs by whether they have a possiblePlugging prediction
  const normalizePlugging = (val: string): 'true' | 'false' | 'none' => {
    const v = (val || '').toString().trim().toLowerCase();
    if (['yes','true','y','1'].includes(v)) return 'true';
    if (['no','false','n','0'].includes(v)) return 'false';
    return 'none';
  };

  const { rigsPluggingTrue, rigsPluggingFalse } = useMemo(() => {
    const trueList: string[] = [];
    const falseList: string[] = [];
    rigs.forEach(r => {
      const a = assessments[r]?.data;
      const pluggingRaw = getAssessmentField(a, 'possiblePlugging');
      const norm = normalizePlugging(pluggingRaw);
      if (norm === 'true') trueList.push(r); else falseList.push(r); // include false and none in second group
    });
    return { rigsPluggingTrue: trueList, rigsPluggingFalse: falseList };
  }, [rigs, assessments]);


  useEffect(() => {
    if (!rigs.length) return;
    const missing = rigs.filter(r => !assessments[r]);
    if (!missing.length) return;
    (async () => {
      try {
        const payload: any = { RigNames: missing };
        if (selectedLocation) payload.Location = selectedLocation;
        const resp = await callApi('/api/RigAssessments/latest-many', { method: 'POST', body: JSON.stringify(payload) });
        if (!resp.ok) return;
        const data = await resp.json();
        setAssessments(prev => {
          const next = { ...prev } as Record<string, AssessmentState>;
          missing.forEach(r => { if (!next[r]) next[r] = { loading: false }; });
          Object.keys(data || {}).forEach(k => { next[k] = { data: data[k], loading: false }; });
          return next;
        });
      } catch { /* ignore */ }
    })();
  }, [rigs, selectedLocation]);

  const resolvePluggingColor = (rig: string): string => {
    const a = assessments[rig]?.data;
    const raw = getAssessmentField(a, 'possiblePlugging').toLowerCase();
    if (['yes','true','y','1'].includes(raw)) return 'success.main';
    if (['no','false','n','0'].includes(raw)) return 'error.main';
    return 'text.secondary';
  };

  const runAssessment = async (rig: string) => {
    setAssessments(prev => ({ ...prev, [rig]: { ...prev[rig], loading: true, error: undefined } }));
    try {
      const body: any = { RigName: rig, HistoryDays: 60 };
      if (selectedLocation) body.Location = selectedLocation;
      const resp = await callApi('/api/RigAssessments/run', { method: 'POST', body: JSON.stringify(body) });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Run failed');
      }
      const data = await resp.json();
      setAssessments(prev => ({ ...prev, [rig]: { data, loading: false } }));
    } catch (e: any) {
      setAssessments(prev => ({ ...prev, [rig]: { ...prev[rig], loading: false, error: e?.message || 'Failed' } }));
    }
  };

  const missingPredictions = useMemo(() => {
    // rigs without any prediction (none) only
    return rigs.filter(r => {
      const a = assessments[r]?.data;
      const pluggingRaw = getAssessmentField(a, 'possiblePlugging');
      return normalizePlugging(pluggingRaw) === 'none';
    });
  }, [rigs, assessments]);

  const queueDayAssessments = async () => {
    try {
      setQueueing(true);
      const payload: any = { Date: format(selectedDate, 'yyyy-MM-dd') };
      if (selectedLocation) payload.Location = selectedLocation;
      await callApi('/api/RigAssessments/run-missing-for-date', { method: 'POST', body: JSON.stringify(payload) });
    } catch { /* ignore */ } finally { setQueueing(false); }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
      <Box>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={4} lg={3}>
            <Button
              variant="outlined"
              startIcon={<CalendarMonthIcon />}
              onClick={handleDateClick}
              sx={{
                width: '100%',
                backgroundColor: !isSameDay(selectedDate, new Date()) ? 'rgba(255, 152, 0, 0.1)' : 'inherit'
              }}
            >
              {format(selectedDate, 'yyyy-MM-dd')}
            </Button>
            <Popover open={open} anchorEl={anchorEl} onClose={handleDateClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                styles={{
                  caption: { color: 'white' },
                  day_selected: { backgroundColor: 'white', color: 'black' },
                  day_today: { color: '#4caf50' },
                  nav_button: { color: 'white' }
                }}
              />
            </Popover>
          </Grid>
          {availableLocations.length > 1 && (
            <Grid item xs={12} md={4} lg={3}>
              <FormControl fullWidth>
                <InputLabel id="forecast-location-label">Location</InputLabel>
                <Select
                  labelId="forecast-location-label"
                  label="Location"
                  value={selectedLocation || ''}
                  onChange={(e) => setSelectedLocation(e.target.value || null)}
                  startAdornment={<LocationOnIcon sx={{ mr: 1 }} />}
                  sx={{ backgroundColor: selectedLocation ? 'rgba(255, 152, 0, 0.1)' : 'inherit' }}
                >
                  {availableLocations.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={4} lg={3}>
            {missingPredictions.length > 0 && (
              <Tooltip title="Queue assessments for rigs missing predictions">
                <span>
                  <Button
                    variant="contained"
                    startIcon={<LightbulbOutlinedIcon sx={{ color: '#ffeb3b' }} />}
                    disabled={queueing}
                    onClick={queueDayAssessments}
                    sx={{
                      textTransform: 'none',
                      backgroundColor: 'transparent',
                      color: 'white',
                      border: '1px solid white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    {queueing ? 'Queueing…' : 'Run predictions for day'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Box>
      {/* Table: Rigs with PossiblePlugging === TRUE */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Forecast - Plugging = TRUE</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Rigs: {rigsPluggingTrue.length} / {rigs.length}</Typography>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Rig</TableCell>
                <TableCell>PossiblePlugging</TableCell>
                <TableCell>Phase</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>ReasonPlugging</TableCell>
                <TableCell>RunDate</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rigsPluggingTrue.map(rig => {
                const state = assessments[rig];
                const a = state?.data;
                const plugging = getAssessmentField(a, 'possiblePlugging');
                const phase = getAssessmentField(a, 'phase');
                const reason = getAssessmentField(a, 'reason');
                const reasonPlugging = getAssessmentField(a, 'reasonPlugging');
                const runDate = getAssessmentField(a, 'runDate');
                const isExpanded = expanded.has(rig);
                const reportForRig = reports.find(r => r.Rig === rig);
                return (
                  <React.Fragment key={rig}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => setExpanded(prev => { const n = new Set(prev); n.has(rig) ? n.delete(rig) : n.add(rig); return n; })}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{rig}</TableCell>
                      <TableCell sx={{ color: resolvePluggingColor(rig) }}>{plugging || '-'}</TableCell>
                      <TableCell>{phase || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>{reason || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>{reasonPlugging || '-'}</TableCell>
                      <TableCell>{runDate ? new Date(runDate).toLocaleString() : '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={state?.error || 'Run / refresh assessment'}>
                          <span>
                            <IconButton size="small" disabled={state?.loading} onClick={() => runAssessment(rig)}>
                              {state?.loading ? <CircularProgress size={16} /> : <AssessmentIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)' }}>
                            <Typography variant="subtitle2" gutterBottom>Report Context</Typography>
                            {reportForRig ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {reportForRig.Last24 && <Typography variant="body2"><strong>Last24:</strong> {reportForRig.Last24}</Typography>}
                                {reportForRig.Next24 && <Typography variant="body2"><strong>Next24:</strong> {reportForRig.Next24}</Typography>}
                                {reportForRig.Hole && <Typography variant="body2"><strong>Hole:</strong> {reportForRig.Hole}</Typography>}
                                {reportForRig.Manager && <Typography variant="body2"><strong>Manager:</strong> {reportForRig.Manager}</Typography>}
                                {reportForRig.FileUrl && <Button size="small" variant="outlined" href={reportForRig.FileUrl} target="_blank" rel="noopener noreferrer">Open Report</Button>}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No report context available.</Typography>
                            )}
                            {a && getAssessmentField(a,'contextSample') && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Context Sample</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{getAssessmentField(a,'contextSample')}</Typography>
                              </Box>
                            )}
                            {a && getAssessmentField(a,'prompt') && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Prompt</Typography>
                                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>{getAssessmentField(a,'prompt')}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              {!rigsPluggingTrue.length && (
                <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No predictions yet.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Table: Rigs with PossiblePlugging !== TRUE (FALSE or NONE) collapsible */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Forecast - Plugging = FALSE / NONE</Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => { setShowFalse((prev: boolean) => !prev); }}
            sx={{ textTransform: 'none' }}
          >
            {showFalse ? 'Hide' : 'Show'} ({rigsPluggingFalse.length})
          </Button>
        </Box>
        <Collapse in={showFalse} timeout="auto" unmountOnExit>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Rigs: {rigsPluggingFalse.length} / {rigs.length}</Typography>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Rig</TableCell>
                <TableCell>PossiblePlugging</TableCell>
                <TableCell>Phase</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>ReasonPlugging</TableCell>
                <TableCell>RunDate</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rigsPluggingFalse.map(rig => {
                const state = assessments[rig];
                const a = state?.data;
                const plugging = getAssessmentField(a, 'possiblePlugging');
                const phase = getAssessmentField(a, 'phase');
                const reason = getAssessmentField(a, 'reason');
                const reasonPlugging = getAssessmentField(a, 'reasonPlugging');
                const runDate = getAssessmentField(a, 'runDate');
                const isExpanded = expanded.has(rig);
                const reportForRig = reports.find(r => r.Rig === rig);
                return (
                  <React.Fragment key={rig}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => setExpanded(prev => { const n = new Set(prev); n.has(rig) ? n.delete(rig) : n.add(rig); return n; })}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{rig}</TableCell>
                      <TableCell sx={{ color: resolvePluggingColor(rig) }}>{plugging || '-'}</TableCell>
                      <TableCell>{phase || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>{reason || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>{reasonPlugging || '-'}</TableCell>
                      <TableCell>{runDate ? new Date(runDate).toLocaleString() : '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={state?.error || 'Run / refresh assessment'}>
                          <span>
                            <IconButton size="small" disabled={state?.loading} onClick={() => runAssessment(rig)}>
                              {state?.loading ? <CircularProgress size={16} /> : <AssessmentIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)' }}>
                            <Typography variant="subtitle2" gutterBottom>Report Context</Typography>
                            {reportForRig ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {reportForRig.Last24 && <Typography variant="body2"><strong>Last24:</strong> {reportForRig.Last24}</Typography>}
                                {reportForRig.Next24 && <Typography variant="body2"><strong>Next24:</strong> {reportForRig.Next24}</Typography>}
                                {reportForRig.Hole && <Typography variant="body2"><strong>Hole:</strong> {reportForRig.Hole}</Typography>}
                                {reportForRig.Manager && <Typography variant="body2"><strong>Manager:</strong> {reportForRig.Manager}</Typography>}
                                {reportForRig.FileUrl && <Button size="small" variant="outlined" href={reportForRig.FileUrl} target="_blank" rel="noopener noreferrer">Open Report</Button>}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No report context available.</Typography>
                            )}
                            {a && getAssessmentField(a,'contextSample') && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Context Sample</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{getAssessmentField(a,'contextSample')}</Typography>
                              </Box>
                            )}
                            {a && getAssessmentField(a,'prompt') && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Prompt</Typography>
                                <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>{getAssessmentField(a,'prompt')}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
      {!rigsPluggingFalse.length && (
                <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">All rigs have predictions.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
    </TableContainer>
    </Collapse>
      </Paper>
    </Box>
  );
};

export default Forecast;
